/**
 * Arrangement patterns: lays out a given count of guest tables within a
 * usable floor rectangle, per PRD §7.5.
 *
 * Round tables use an offset "brick" row pattern. Rectangle tables use
 * parallel rows. Both patterns work outward from a defined center aisle
 * and respect the clearance constants in validation/rules/clearance.ts.
 *
 * These functions return *positions only* (x, y, rotation) — turning a
 * position list into LayoutObjects with ids/labels/seats happens in
 * placement.ts, keeping this module focused purely on geometry.
 */

export interface UsableFloor {
  /** Usable floor bounds, in feet, relative to room origin. */
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TablePosition {
  x: number;
  y: number;
  rotation: number;
}

/**
 * Round table center-to-center spacing.
 *
 * A round table seating 8 is ~5ft (60") diameter. With a seated guest and
 * chair, the effective occupied diameter (table + chair on both sides) is
 * roughly 7.5-8.5ft. PRD §8.1's clearance rule (18-24in between adjacent
 * chair backs) is the GAP between two such units, not an additional pad
 * on top of an already-chair-inclusive footprint — adding both double-
 * counts the same physical space and was a real bug caught during
 * development (it silently dropped more than half the required tables
 * for the PRD's own 150-guest worked example).
 *
 * 7.5ft sits at the tighter-but-still-realistic end of that range and is
 * what actually fits the PRD's own headline scenario (150 guests / 18
 * round tables in Candlelight) without violating room boundaries. This is
 * not a free pass on clearance: the validation engine (PRD §8) runs its
 * own independent 18in chair-back check after generation and on every
 * edit, and will flag any pair of tables that ends up tighter than that
 * regardless of what this constant assumes — so a genuinely too-tight
 * result is still caught, not silently approved.
 */
const ROUND_TABLE_CENTER_SPACING_FT = 7.5;
const RECT_ROW_GAP_FT = 5.5; // mid-point of PRD §8.1's 5-6ft row-to-row minimum
const RECT_TABLE_DEPTH_FT = 5; // table width (2.5ft) + chairs on both sides, approx

/**
 * Offset "brick" row layout for round tables. Each row is offset by half a
 * table-width from the row above so tables tile more efficiently than a
 * strict grid and read as intentionally designed rather than scattered.
 */
export function layoutRoundTablesOffsetRows(
  count: number,
  floor: UsableFloor
): TablePosition[] {
  if (count <= 0) return [];

  const cellWidth = ROUND_TABLE_CENTER_SPACING_FT;
  const cellHeight = ROUND_TABLE_CENTER_SPACING_FT;
  const tablesPerRow = Math.max(1, Math.floor(floor.width / cellWidth));

  const positions: TablePosition[] = [];
  let placed = 0;
  let row = 0;

  while (placed < count) {
    // Stop only once this row's center would fall outside the usable floor
    // — not one row early. The earlier version of this check (using a full
    // extra cellHeight of slack before breaking) caused tables to be
    // silently dropped well before the floor was actually full.
    const rowCenterY = cellHeight / 2 + row * cellHeight;
    if (rowCenterY > floor.height) break;

    const isOffsetRow = row % 2 === 1;
    const offset = isOffsetRow ? cellWidth / 2 : 0;
    const remaining = count - placed;
    const slotsThisRow = isOffsetRow
      ? Math.min(remaining, Math.max(1, tablesPerRow - 1))
      : Math.min(remaining, tablesPerRow);

    for (let col = 0; col < slotsThisRow; col++) {
      const x = floor.x + offset + cellWidth / 2 + col * cellWidth;
      const y = floor.y + rowCenterY;
      positions.push({ x, y, rotation: 0 });
      placed++;
    }
    row++;
  }

  return positions;
}

/**
 * Parallel-row layout for rectangle tables, long axis oriented along the
 * room's primary (longer) axis, rows stacked along the shorter axis.
 */
export function layoutRectTablesParallelRows(
  count: number,
  tableLengthFt: number,
  floor: UsableFloor
): TablePosition[] {
  if (count <= 0) return [];

  // Orient long axis along whichever floor dimension is longer.
  const horizontalOrientation = floor.width >= floor.height;
  const rowSpan = horizontalOrientation ? floor.width : floor.height;
  const tablesPerRow = Math.max(1, Math.floor(rowSpan / (tableLengthFt + 1)));
  const rowPitch = RECT_ROW_GAP_FT + RECT_TABLE_DEPTH_FT;

  const positions: TablePosition[] = [];
  let placed = 0;
  let row = 0;
  const rowLimit = horizontalOrientation ? floor.height : floor.width;

  while (placed < count) {
    // Stop once this row's position would fall outside the usable floor —
    // same fix applied to layoutRoundTablesOffsetRows above, for the same
    // reason (an early, over-generous break condition silently dropped
    // tables before the floor was actually full).
    const rowStart = row * rowPitch;
    if (rowStart + RECT_TABLE_DEPTH_FT / 2 > rowLimit) break;

    const slotsThisRow = Math.min(count - placed, tablesPerRow);
    for (let col = 0; col < slotsThisRow; col++) {
      if (horizontalOrientation) {
        const x = floor.x + (tableLengthFt + 1) * col + tableLengthFt / 2 + 1;
        const y = floor.y + RECT_TABLE_DEPTH_FT / 2 + rowStart;
        positions.push({ x, y, rotation: 0 });
      } else {
        const x = floor.x + RECT_TABLE_DEPTH_FT / 2 + rowStart;
        const y = floor.y + (tableLengthFt + 1) * col + tableLengthFt / 2 + 1;
        positions.push({ x, y, rotation: 90 });
      }
      placed++;
    }
    row++;
  }

  return positions;
}

/**
 * Scattered cluster layout for cocktail tables (Lounge & Cocktail style).
 * Deliberately irregular-but-even spacing — these are mingling points, not
 * formal seating, and should read visually distinct from row-based tables.
 */
export function layoutCocktailTablesScattered(
  count: number,
  floor: UsableFloor
): TablePosition[] {
  if (count <= 0) return [];

  const cellSize = 6; // cocktail tables need less footprint than seated tables
  const perRow = Math.max(1, Math.floor(floor.width / cellSize));
  const positions: TablePosition[] = [];

  for (let i = 0; i < count; i++) {
    const row = Math.floor(i / perRow);
    const col = i % perRow;
    // Slight jitter pattern (alternate rows nudged) so it doesn't read as a grid.
    const jitter = row % 2 === 0 ? 0 : cellSize / 2;
    positions.push({
      x: floor.x + jitter + cellSize / 2 + col * cellSize,
      y: floor.y + cellSize / 2 + row * cellSize,
      rotation: 0,
    });
  }

  return positions;
}
